import { useState } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useData } from '@/contexts/DataContext';
import { useBaby } from '@/contexts/BabyContext';
import { getWHOTable, ageInMonths, getPercentile } from '@/lib/who-growth';

/* ─── Types ─────────────────────────────────────────────── */
type GrowthTab = 'weight' | 'height' | 'hc';

const TABS: { id: GrowthTab; label: string; unit: string; color: string }[] = [
  { id: 'weight', label: 'Weight', unit: 'kg', color: '#f97316' },
  { id: 'height', label: 'Height', unit: 'cm', color: '#8b5cf6' },
  { id: 'hc',     label: 'HC',     unit: 'cm', color: '#0ea5e9' },
];

const PERCENTILE_COLOR = '#94a3b8';

/* ─── Helpers ───────────────────────────────────────────── */
function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}

function buildChartData(
  metric: 'weight' | 'height' | 'hc',
  gender: 'boy' | 'girl',
  dob: Date,
  measurements: { date: Date; value: number }[]
) {
  const maxAgeMonths = Math.min(24, Math.ceil(ageInMonths(dob, new Date())));
  const table = getWHOTable(metric, gender);
  const snap  = 0.5;

  const points: Record<string, any>[] = [];
  for (let m = 0; m <= maxAgeMonths; m += snap) {
    const month = Math.round(m * 10) / 10;
    const idx   = Math.min(23, Math.floor(month));
    const frac  = month - idx;
    const lo    = table[idx];
    const hi    = table[Math.min(24, idx + 1)];
    const lerp  = (a: number, b: number) => Math.round((a + (b - a) * frac) * 10) / 10;
    points.push({
      month,
      p3:  lerp(lo.p3,  hi.p3),
      p10: lerp(lo.p10, hi.p10),
      p50: lerp(lo.p50, hi.p50),
      p97: lerp(lo.p97, hi.p97),
    });
  }

  for (const m of measurements) {
    const ageMo = Math.round(ageInMonths(dob, m.date) * 10) / 10;
    if (ageMo < 0 || ageMo > 24.5) continue;
    const nearest = points.reduce((best, pt) =>
      Math.abs(pt.month - ageMo) < Math.abs(best.month - ageMo) ? pt : best
    );
    if (Math.abs(nearest.month - ageMo) < 0.3) {
      nearest.baby = m.value;
    } else {
      points.push({ month: ageMo, baby: m.value });
      points.sort((a, b) => a.month - b.month);
    }
  }

  return points;
}

/* ─── Component ─────────────────────────────────────────── */
const GrowthChartWidget = () => {
  const { activeBaby } = useBaby();
  const { data } = useData();
  const [activeTab, setActiveTab] = useState<GrowthTab>('weight');

  if (!activeBaby) return null;

  const dob           = new Date(activeBaby.dob);
  const gender        = activeBaby.gender;
  const babyFirstName = activeBaby.name.split(' ')[0];
  const currentAgeMo  = ageInMonths(dob, new Date());
  const tabCfg        = TABS.find(t => t.id === activeTab)!;

  const metricKey = activeTab === 'hc' ? 'headCircumference' : activeTab;
  const measurements = data.growth
    .filter(g => g.metric === metricKey)
    .map(g => ({ date: new Date(g.date), value: g.value }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  const chartData = buildChartData(activeTab, gender, dob, measurements);

  const latestMeas = measurements.at(-1);
  const firstMeas  = measurements[0];

  const currentPct = latestMeas
    ? getPercentile(latestMeas.value, ageInMonths(dob, latestMeas.date), activeTab, gender)
    : null;
  const birthPct = (firstMeas && ageInMonths(dob, firstMeas.date) < 1)
    ? getPercentile(firstMeas.value, 0, activeTab, gender)
    : null;

  const ticks = Array.from(
    { length: Math.min(25, Math.ceil(currentAgeMo) + 1) },
    (_, i) => i
  ).filter((_, i) => i % 2 === 0);

  return (
    <div className="bg-card rounded-3xl p-5 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-foreground">📊 Growth Chart</h3>
        <span className="text-xs text-muted-foreground">WHO Standards</span>
      </div>

      {/* Tab selector */}
      <div
        className="flex gap-1 mb-4 p-1 rounded-xl"
        style={{ background: 'hsl(var(--muted))' }}
      >
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all"
            style={activeTab === tab.id
              ? { background: 'white', color: tab.color, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }
              : { background: 'transparent', color: 'hsl(var(--muted-foreground))' }
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {measurements.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">
          No {tabCfg.label.toLowerCase()} measurements yet
        </p>
      ) : (
        <>
          {/* Percentile summary */}
          {(birthPct !== null || currentPct !== null) && (
            <div className="bg-muted/40 rounded-2xl px-4 py-1 mb-3 divide-y divide-border/30">
              {birthPct !== null && firstMeas && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-xs text-muted-foreground">At birth</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">
                      {firstMeas.value} {tabCfg.unit}
                    </span>
                    <span
                      className="text-xs font-bold px-2 py-0.5 rounded-full"
                      style={birthPct < 10 || birthPct > 90
                        ? { background: 'hsl(var(--feed-bg))', color: 'hsl(var(--feed-accent))' }
                        : { background: 'hsl(150,50%,93%)', color: 'hsl(150,50%,32%)' }}
                    >
                      {ordinal(birthPct)} %ile
                    </span>
                  </div>
                </div>
              )}
              {currentPct !== null && latestMeas && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-xs text-muted-foreground">Current</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">
                      {latestMeas.value} {tabCfg.unit}
                    </span>
                    <span
                      className="text-xs font-bold px-2 py-0.5 rounded-full"
                      style={currentPct < 10 || currentPct > 90
                        ? { background: 'hsl(var(--feed-bg))', color: 'hsl(var(--feed-accent))' }
                        : { background: 'hsl(150,50%,93%)', color: 'hsl(150,50%,32%)' }}
                    >
                      {ordinal(currentPct)} %ile
                    </span>
                  </div>
                </div>
              )}
              {currentPct !== null && birthPct !== null && (
                <div className="py-2">
                  <p className="text-xs text-muted-foreground text-center">
                    {currentPct > birthPct
                      ? `📈 Moved up from ${ordinal(birthPct)} to ${ordinal(currentPct)} percentile`
                      : currentPct < birthPct
                        ? `📉 Moved from ${ordinal(birthPct)} to ${ordinal(currentPct)} percentile`
                        : `Staying steady at ${ordinal(currentPct)} percentile`}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Chart */}
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" opacity={0.6} />
                <XAxis
                  dataKey="month"
                  type="number"
                  domain={[0, Math.min(24, Math.ceil(currentAgeMo))]}
                  tick={{ fontSize: 9 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `${v}m`}
                  ticks={ticks}
                />
                <YAxis
                  tick={{ fontSize: 9 }}
                  tickLine={false}
                  axisLine={false}
                  width={28}
                />
                <Tooltip
                  formatter={(v: any, name: string | undefined) => {
                    const labels: Record<string, string> = {
                      p3: 'P3', p10: 'P10', p50: 'P50', p97: 'P97',
                      baby: babyFirstName,
                    };
                    return [`${v} ${tabCfg.unit}`, labels[name ?? ''] ?? name];
                  }}
                  labelFormatter={(l) => `Age ${l}m`}
                />
                {/* WHO percentile bands */}
                <Line dataKey="p97" stroke={PERCENTILE_COLOR} strokeOpacity={0.5} strokeWidth={1} dot={false} strokeDasharray="4 3" connectNulls name="p97" />
                <Line dataKey="p50" stroke={PERCENTILE_COLOR} strokeOpacity={0.5} strokeWidth={1} dot={false} strokeDasharray="4 3" connectNulls name="p50" />
                <Line dataKey="p10" stroke={PERCENTILE_COLOR} strokeOpacity={0.5} strokeWidth={1} dot={false} strokeDasharray="4 3" connectNulls name="p10" />
                <Line dataKey="p3"  stroke={PERCENTILE_COLOR} strokeOpacity={0.5} strokeWidth={1} dot={false} strokeDasharray="4 3" connectNulls name="p3"  />
                {/* Baby's measurements */}
                <Line
                  dataKey="baby"
                  stroke={tabCfg.color}
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: tabCfg.color, stroke: 'white', strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                  connectNulls
                  name="baby"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-1.5">
              <svg width="20" height="6" className="flex-shrink-0">
                <line x1="0" y1="3" x2="20" y2="3"
                  stroke={PERCENTILE_COLOR} strokeWidth="1.5"
                  strokeDasharray="4 3" strokeOpacity="0.7" />
              </svg>
              <span className="text-xs text-muted-foreground">3rd, 10th, 50th, 97th %ile</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-0.5 rounded flex-shrink-0" style={{ background: tabCfg.color }} />
              <span className="text-xs text-muted-foreground">{babyFirstName}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GrowthChartWidget;
